import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  Baby, Lock, CreditCard, Shield, Check, ArrowRight, 
  LogOut, ExternalLink, AlertCircle, Loader2
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { toast } from 'sonner';

import API from '@/config/api';
// API imported from config;

const SubscriptionGatePage = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const { subscription, refreshSubscription } = useSubscription();
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  const handleSyncFromStripe = async () => {
    setSyncLoading(true);
    try {
      const res = await axios.post(
        `${API}/subscriptions/sync-stripe`,
        {},
        getAuthHeaders()
      );
      
      if (res.data.synced) {
        toast.success('Subscription synced from Stripe!');
        // Refresh subscription context
        if (refreshSubscription) {
          await refreshSubscription();
        }
        // Navigate to dashboard
        navigate('/admin');
      } else {
        toast.info(res.data.message || 'No existing subscription found in Stripe');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync subscription');
    } finally {
      setSyncLoading(false);
    }
  };

  const plans = [
    { id: 'basic', name: 'Basic', price: 99, children: 10, staff: 2 },
    { id: 'professional', name: 'Professional', price: 149, children: 49, staff: 10, popular: true },
    { id: 'enterprise', name: 'Enterprise', price: 249, children: 100, staff: 15 }
  ];

  const handleSubscribe = async (planId) => {
    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions first');
      return;
    }

    setLoading(true);
    try {
      // First, record terms acceptance
      await axios.post(`${API}/auth/accept-terms`, {}, getAuthHeaders());

      // Then create checkout session
      const res = await axios.post(
        `${API}/subscriptions/checkout`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        getAuthHeaders()
      );
      
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start subscription');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Check if subscription exists but is canceled/expired
  const hasExpiredSub = subscription && subscription.status !== 'active';

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 to-background" data-testid="subscription-gate">
      {/* Header */}
      <div className="border-b bg-white/80 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <Baby className="w-6 h-6 text-primary-foreground" />
            </div>
            <span className="font-heading font-bold text-xl">CareKids WI</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              Logged in as <strong>{user?.email}</strong>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Status Card */}
        <Card className="mb-8 border-amber-200 bg-amber-50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-amber-100 rounded-full">
                <Lock className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl mb-2">
                  {hasExpiredSub ? 'Subscription Expired' : 'Subscription Required'}
                </h2>
                <p className="text-muted-foreground">
                  {hasExpiredSub 
                    ? 'Your subscription has expired. Please renew to continue accessing the platform.'
                    : 'To access the CareKids WI platform as an administrator or provider, you need an active subscription.'
                  }
                </p>
                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleSyncFromStripe}
                    disabled={syncLoading}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100"
                  >
                    {syncLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Already have a subscription? Click to sync
                      </>
                    )}
                  </Button>
                </div>
                {hasExpiredSub && subscription && (
                  <div className="mt-3 flex items-center gap-2">
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      <AlertCircle className="w-3 h-3 mr-1" />
                      {subscription.status === 'canceled' ? 'Canceled' : 'Expired'}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Previous plan: {subscription.plan_name}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms Acceptance */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Terms and Conditions
            </CardTitle>
            <CardDescription>
              Please review and accept our terms before subscribing
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-48 rounded-lg border p-4 mb-4">
              <div className="text-sm text-muted-foreground space-y-3">
                <p>
                  <strong>1. Service Agreement:</strong> By subscribing to CareKids WI, you agree to use the platform
                  in accordance with Wisconsin DCF regulations and all applicable laws.
                </p>
                <p>
                  <strong>2. Data Protection:</strong> You are responsible for the accuracy of child and staff data
                  entered into the system. CareKids WI handles all data in compliance with privacy regulations.
                </p>
                <p>
                  <strong>3. Payment Terms:</strong> Subscriptions are billed monthly. You may cancel at any time,
                  and your access will continue until the end of the billing period.
                </p>
                <p>
                  <strong>4. Child Data:</strong> You confirm that you have obtained proper consent from parents/guardians
                  for managing their children&apos;s information on this platform.
                </p>
                <p>
                  <strong>5. Compliance:</strong> You are responsible for maintaining compliance with all state and
                  federal regulations regarding child care operations.
                </p>
              </div>
            </ScrollArea>
            
            <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
              <Checkbox 
                id="terms" 
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-0.5"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                I have read and agree to the{' '}
                <Link to="/terms" target="_blank" className="text-primary hover:underline font-semibold">
                  Terms and Conditions <ExternalLink className="w-3 h-3 inline" />
                </Link>
                {' '}and{' '}
                <Link to="/privacy" target="_blank" className="text-primary hover:underline font-semibold">
                  Privacy Policy <ExternalLink className="w-3 h-3 inline" />
                </Link>
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Pricing Plans */}
        <h3 className="font-heading font-bold text-2xl mb-6 text-center">Choose Your Plan</h3>
        
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card 
              key={plan.id}
              className={`relative ${plan.popular ? 'ring-2 ring-primary shadow-lg' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge className="bg-primary">Most Popular</Badge>
                </div>
              )}
              <CardHeader className="text-center pb-2">
                <CardTitle>{plan.name}</CardTitle>
                <div className="mt-2">
                  <span className="text-3xl font-bold">${plan.price}</span>
                  <span className="text-muted-foreground">/month</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Up to {plan.children} children</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Up to {plan.staff} staff accounts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>DCF compliance tools</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    <span>Parent portal access</span>
                  </div>
                </div>
                
                <Button 
                  className={`w-full ${plan.popular ? 'btn-primary' : ''}`}
                  variant={plan.popular ? 'default' : 'outline'}
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading || !termsAccepted}
                  data-testid={`subscribe-${plan.id}`}
                >
                  {loading ? 'Processing...' : 'Subscribe'}
                  <CreditCard className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-muted-foreground mt-8">
          Need help? <a href="mailto:support@carekidswi.com" className="text-primary hover:underline">Contact support</a>
        </p>
      </div>
    </div>
  );
};

export default SubscriptionGatePage;
